// XNGAGE CONFIDENTIAL
// __________________________
//
// Copyright (C) 2022 Xngage - All Rights Reserved
//
// All code or information contained herein is, and remains the
// property of Xngage LLC and its customers.  The intellectual
// and technical concepts contained are proprietary to Xngage LLC
// and may be covered by U.S. and Foreign Patents, patents in
// process, and are protected by trade secret or copyright law.
// Dissemination of this information or reproduction of this material
// is strictly forbidden unless prior written permission is obtained
// from Xngage LLC.
//

import { TippyPopupStyle } from "@hardwoods/App/Components/XngTooltip/Interface";
import XngTooltip from "@hardwoods/App/Components/XngTooltip/XngTooltip";
import Info from "@hardwoods/XDL/Icons/Info";
import defaultTooltip from "@hardwoods/XDL/PreStyle/tooltip";
import { mergeWithCss } from "@hardwoods/XDL/Utilities/mergeWithCss";
import Icon, { IconPresentationProps } from "@insite/mobius/Icon";
import { TypographyPresentationProps } from "@insite/mobius/Typography";
import applyPropBuilder from "@insite/mobius/utilities/applyPropBuilder";
import get from "@insite/mobius/utilities/get";
import { StyledProp } from "@insite/mobius/utilities/InjectableCss";
import MobiusStyledComponentProps from "@insite/mobius/utilities/MobiusStyledComponentProps";
import * as React from "react";
import { FC } from "react";
import { css, withTheme } from "styled-components";

export interface TooltipPresentationProps {
    /** CSS strings or styled-components functions to be injected into nested components. These will override the theme defaults.
     * @themable */
    cssOverrides?: {
        tooltipClickable?: StyledProp<TooltipProps>;
        tooltipBody?: StyledProp<TooltipProps>;
        tooltipContainer?: StyledProp<TooltipProps>;
        tooltipWrapper?: StyledProp<TooltipProps>;
        tooltipArrow?: StyledProp<TooltipProps>;
    };
    /** Props to be passed into the trigger Icon component if no triggerComponent is provided.
     * @themable */
    iconProps?: IconPresentationProps;
    /** Props that will be passed to the typography component rendered in the tooltip.
     * @themable */
    typographyProps?: TypographyPresentationProps;
}

export type TooltipComponentProps = MobiusStyledComponentProps<
    "span",
    {
        /** Component to be used as the trigger to optionally replace the default trigger icon. */
        triggerComponent?: React.ReactElement;
        /** The text that will appear in the tooltip */
        text: string;
        /** Function called on tooltip open */
        onOpen?: React.EventHandler<React.SyntheticEvent>;
        /** Function called on tooltip close */
        onClose?: React.EventHandler<React.SyntheticEvent>;
        /** Text to be used to describe the tooltip trigger. Used for screenreaders. Defaults to "more info" */
        triggerAltText?: string;
    }
>;

type TooltipProps = TooltipComponentProps & TooltipPresentationProps;

const Tooltip: FC<TooltipProps> = props => {
    const { text } = props;
    const { spreadProps } = applyPropBuilder(props, { component: "tooltip" });
    const cssOverrides = mergeWithCss(defaultTooltip.defaultProps?.cssOverrides, spreadProps("cssOverrides"));
    const styles: TippyPopupStyle = {
        main: {
            css: css`
                ${get(cssOverrides, "tooltipContainer")}
            `,
        },
    };

    return (
        <XngTooltip content={text} extendedStyles={styles}>
            <Icon src={Info} size={20} color="text.main" {...spreadProps("iconProps")} />
        </XngTooltip>
    );
};

/** @component */
export default withTheme(Tooltip);
